/**
 * V4RightRail — Phase 7c-3.
 *
 * The single source of right-side chrome in V4. Replaces FOUR floating
 * panels that used to compete for the same screen real estate:
 *
 *   • V4Inspector       — anchored, always present (320px wide)
 *   • V4ValidationPanel — floating overlay top-right (z=8)
 *   • V4RunDetailPanel  — floating overlay top-right (z=9)
 *   • V4AIBuilder       — floating gradient chip bottom-right (z=11)
 *
 * They're now all rendered INSIDE this rail, with a vertical 40px icon
 * strip on the left edge picking which one is visible. Only ONE active
 * surface at a time — the user feedback called out the prior stacking
 * as "scattered," and a single-active-panel rail is the canonical fix
 * (Linear, Figma, Vercel, Cursor — every modern pro tool does this).
 *
 *   ┌──┬────────────────────┐
 *   │🔍│ INSPECTOR          │  ← icon-strip + panel body
 *   │⚠2│ ───────────────    │
 *   │▶ │  Panel content      │
 *   │✨│  switches based on  │
 *   │📋│  active mode        │
 *   └──┴────────────────────┘
 *
 * Mode auto-switching:
 *   • Selecting a node → Inspector (if mode was idle)
 *   • Click validation chip in toolbar → Validation
 *   • Click Run / Runs row → Run detail
 *   • ⌘I → AI Builder
 *   • Click JSON tab in toolbar → JSON view
 *
 * Bedrock contract: each panel mode is its own component, this file is
 * just the chrome + router. Adding a new mode = add an icon + a case
 * in the body switch.
 */

import React, { useEffect, useMemo } from 'react';
import {
  Settings2, AlertTriangle, AlertOctagon, Play, Sparkles, Braces, X,
  Loader2, History, FileJson,
} from 'lucide-react';
import { BRAND, S, ACCENT, SPACING, RADIUS, FONT, WEIGHT, SHADOW, Z } from './v4Tokens';

// Sub-panels (existing components, re-mounted inside the rail body)
import V4Inspector from './V4Inspector';
import V4ValidationPanel from './V4ValidationPanel';
import V4RunDetailPanel from './V4RunDetailPanel';
import V4AIBuilder from './V4AIBuilder';
import V4JsonView from './V4JsonView';

export const RAIL_MODES = Object.freeze({
  INSPECTOR:  'inspector',
  VALIDATION: 'validation',
  RUN:        'run',
  AI:         'ai',
  JSON:       'json',
});

const MODE_META = [
  { key: RAIL_MODES.INSPECTOR,  icon: Settings2,     label: 'Inspector' },
  { key: RAIL_MODES.VALIDATION, icon: AlertTriangle, label: 'Validation' },
  { key: RAIL_MODES.RUN,        icon: Play,          label: 'Run detail' },
  { key: RAIL_MODES.AI,         icon: Sparkles,      label: 'AI Builder' },
  { key: RAIL_MODES.JSON,       icon: Braces,        label: 'JSON' },
];

const ICON_STRIP_W = 44;
const PANEL_BODY_W = 360;
const RAIL_W       = ICON_STRIP_W + PANEL_BODY_W;

export default function V4RightRail({
  // Controlled mode — host owns it so other surfaces (validation banner,
  // ⌘I hotkey, run-row click) can flip it.
  mode, onModeChange,
  // Inspector
  selectedNode, onNodeUpdate, onNodeDelete, readOnly,
  // Validation
  validation, onJumpToNode,
  // Run detail
  openRunId, onRunClose,
  // AI Builder
  aiBuilderProps,
  // JSON
  payload,
  // Phase 7c-7 — when the bottom dock is showing run detail, hide the
  // Run mode in the rail to avoid duplicate-render of the same panel.
  // CanvasPageV4 sets this to !!openRunId when the dock is mounted.
  hideRunMode = false,
}) {
  // ── Auto-mode hints — gentle, doesn't force the operator off their
  //    current mode. We only nudge in 3 cases:
  //
  //    1. Operator selects a node AND current mode is RUN/AI/JSON
  //       (modes that don't care about selection) → switch to INSPECTOR.
  //       But: if they're explicitly in VALIDATION mode (looking at
  //       errors) we don't yank them away.
  //    2. openRunId arrives AND current mode is INSPECTOR/VALIDATION
  //       (modes that don't care about runs) → switch to RUN.
  //    3. Validation count goes 0 → N AND current mode is INSPECTOR
  //       (operator was editing) → DO NOT auto-switch. The toolbar
  //       chip provides a click target instead.
  useEffect(() => {
    if (!selectedNode) return;
    if (mode === RAIL_MODES.RUN || mode === RAIL_MODES.AI || mode === RAIL_MODES.JSON) {
      onModeChange && onModeChange(RAIL_MODES.INSPECTOR);
    }
  }, [selectedNode?.id]); // eslint-disable-line
  useEffect(() => {
    if (!openRunId) return;
    // Phase 7c-7 — when the bottom dock is showing run detail, we DON'T
    // auto-switch the rail to RUN (the dock handles it). Without this
    // gate, the rail and dock would both render the same panel on every
    // run start, doubling the polling and visually competing.
    if (hideRunMode) return;
    if (mode === RAIL_MODES.INSPECTOR || mode === RAIL_MODES.VALIDATION) {
      onModeChange && onModeChange(RAIL_MODES.RUN);
    }
  }, [openRunId, hideRunMode]); // eslint-disable-line

  // If the rail is somehow in RUN mode while the dock is active, fall
  // back to Inspector. Belt-and-braces — prevents an empty rail body.
  useEffect(() => {
    if (hideRunMode && mode === RAIL_MODES.RUN) {
      onModeChange && onModeChange(RAIL_MODES.INSPECTOR);
    }
  }, [hideRunMode, mode]); // eslint-disable-line

  const errCount  = validation?.stats?.error   || 0;
  const warnCount = validation?.stats?.warning || 0;

  return (
    <aside
      aria-label="Workflow rail"
      style={{
        width: RAIL_W,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        borderLeft: `1px solid ${S[200]}`,
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {/* ── Icon strip ──────────────────────────────────────────── */}
      <nav
        aria-label="Right rail modes"
        style={{
          width: ICON_STRIP_W,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: SPACING.xs,
          padding: `${SPACING.s}px 0`,
          background: S[50],
          borderRight: `1px solid ${S[200]}`,
          flexShrink: 0,
        }}
      >
        {MODE_META
          .filter((m) => !(hideRunMode && m.key === RAIL_MODES.RUN))
          .map((m) => (
            <RailIcon
              key={m.key}
              mode={m}
              active={mode === m.key}
              onClick={() => onModeChange && onModeChange(m.key)}
              badge={badgeFor(m.key, { errCount, warnCount, openRunId })}
            />
          ))}
      </nav>

      {/* ── Panel body ──────────────────────────────────────────── */}
      <div style={{
        width: PANEL_BODY_W,
        height: '100%',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        background: '#ffffff',
      }}>
        {mode === RAIL_MODES.INSPECTOR && (
          <V4Inspector
            selectedNode={selectedNode}
            onUpdate={onNodeUpdate}
            onDelete={onNodeDelete}
            readOnly={readOnly}
            onClose={null /* close X hidden — rail is always present */}
          />
        )}
        {mode === RAIL_MODES.VALIDATION && (
          <RailValidationView
            validation={validation}
            /* User feedback 2026-05-29: clicking "Jump to node" in the
             * issue list left the operator stranded on the Validation
             * view — they wanted to fix the config but couldn't see
             * the Inspector. Now Jump-to-node ALSO flips the rail back
             * to INSPECTOR so they land where they need to edit. */
            onJumpToNode={(id) => {
              if (typeof onJumpToNode === 'function') onJumpToNode(id);
              if (typeof onModeChange === 'function') onModeChange(RAIL_MODES.INSPECTOR);
            }}
            /* Same feedback: the X in the validation header was inert
             * (onClose was nulled with a "rail always present" comment).
             * Wire it to switch back to INSPECTOR so X = collapse-this-
             * view, which is the operator's mental model. */
            onClose={() => onModeChange && onModeChange(RAIL_MODES.INSPECTOR)}
          />
        )}
        {mode === RAIL_MODES.RUN && (
          openRunId
            ? <V4RunDetailPanel
                runId={openRunId}
                onClose={onRunClose}
                onJumpToNode={onJumpToNode}
                embedded
              />
            : <RailEmptyState
                icon={Play}
                title="No run open"
                body="Click Run in the toolbar, or pick a row from the Runs dropdown."
              />
        )}
        {mode === RAIL_MODES.AI && aiBuilderProps && (
          <V4AIBuilder
            {...aiBuilderProps}
            open
            onOpenChange={(open) => { if (!open) onModeChange && onModeChange(RAIL_MODES.INSPECTOR); }}
            embedded
          />
        )}
        {mode === RAIL_MODES.JSON && (
          <V4JsonView
            payload={payload}
            embedded
          />
        )}
      </div>
    </aside>
  );
}

/* ───── icon-strip button with active state + badge ─────────────── */
function RailIcon({ mode, active, onClick, badge }) {
  const Icon = mode.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      title={mode.label}
      aria-label={mode.label}
      aria-pressed={active}
      style={{
        position: 'relative',
        width: 32, height: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: active ? BRAND.dark : S[500],
        background: active ? BRAND.light : 'transparent',
        border: 'none',
        borderRadius: RADIUS.md,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 160ms ease',
      }}
    >
      <Icon size={16} aria-hidden="true" />
      {badge && (
        <span
          style={{
            position: 'absolute',
            top: 2, right: 2,
            minWidth: 14, height: 14,
            padding: '0 4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: badge.tone,
            color: '#ffffff',
            fontSize: 9, fontWeight: WEIGHT.bold,
            borderRadius: RADIUS.pill,
            lineHeight: 1,
          }}
        >
          {badge.label}
        </span>
      )}
    </button>
  );
}

function badgeFor(modeKey, { errCount, warnCount, openRunId }) {
  if (modeKey === RAIL_MODES.VALIDATION) {
    if (errCount > 0)  return { tone: ACCENT.rose.icon,  label: String(errCount) };
    if (warnCount > 0) return { tone: ACCENT.amber.icon, label: String(warnCount) };
  }
  if (modeKey === RAIL_MODES.RUN && openRunId) {
    return { tone: ACCENT.sky.icon, label: '·' };
  }
  return null;
}

/* ───── rail-flavored validation view (re-uses the issue list) ──── */
function RailValidationView({ validation, onJumpToNode, onClose }) {
  const stats = validation?.stats || { total: 0, error: 0, warning: 0, info: 0 };
  if (stats.total === 0) {
    return (
      <RailEmptyState
        icon={AlertTriangle}
        title="Workflow looks clean"
        body="No errors or warnings detected. The validator re-runs on every edit."
      />
    );
  }
  return (
    <V4ValidationPanel
      issues={validation?.issues || []}
      onJumpToNode={onJumpToNode}
      onClose={onClose /* wired by host to switch rail mode back to INSPECTOR */}
      embedded
    />
  );
}

/* ───── empty-state card used by every mode that has nothing to show ─ */
function RailEmptyState({ icon: Icon, title, body }) {
  return (
    <div style={{
      flex: 1,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: SPACING.xl, textAlign: 'center',
      gap: SPACING.s,
    }}>
      <Icon size={22} aria-hidden="true" style={{ color: S[300] }} />
      <div style={{ fontSize: FONT.md, fontWeight: WEIGHT.medium, color: S[700] }}>
        {title}
      </div>
      <div style={{ fontSize: FONT.sm, color: S[500], lineHeight: 1.5, maxWidth: 260 }}>
        {body}
      </div>
    </div>
  );
}
